// React-Query hook over `module_settings`. The wizard bootstraps a row per
// canonical module key for every new tenant (enabled = TRUE for the chosen
// ones, FALSE for the rest), so this query always returns a complete map.
// Components that gate UI on a module just call `useEnabledModules()` and
// check `enabled.has('purchases')`.
//
// Per-user overrides: in addition to the tenant-level `module_settings`
// row, the hook reads `user_module_overrides` for the current user. If an
// override row exists for `(user, module)`, it wins over the tenant
// default — letting us grant or revoke a single module to a single user
// without flipping the tenant-wide setting.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useAuth } from '@/lib/auth-context';
import type { ModuleKey } from '@erp/data-access/interfaces';
import { ALWAYS_ON_MODULES, MODULE_KEYS } from '@erp/data-access/interfaces';

const MODULES_KEY = ['module-settings'] as const;
const OVERRIDES_KEY = ['user-module-overrides'] as const;

interface EnabledModulesResult {
  /** Module-key set whose effective `is_enabled` is true. While the
   *  query is still in flight we return a permissive set (every known key
   *  is included) so the sidebar doesn't flash empty groups on first paint.
   *  Once data arrives we narrow it down to the real enabled set, plus
   *  ALWAYS_ON_MODULES as a floor in case a legacy tenant predates the
   *  per-tenant bootstrap. */
  enabled: Set<ModuleKey>;
  isLoading: boolean;
}

export function useEnabledModules(): EnabledModulesResult {
  const ds = useDataService();
  const { user } = useAuth();

  const tenantQ = useQuery({
    queryKey: MODULES_KEY,
    queryFn: () => ds.settings.getModuleSettings(),
    staleTime: 5 * 60 * 1000,
    enabled: !!user,
  });

  const overrideQ = useQuery({
    queryKey: [...OVERRIDES_KEY, user?.id ?? null] as const,
    queryFn: () => ds.settings.getUserModuleOverrides(),
    staleTime: 5 * 60 * 1000,
    enabled: !!user,
    // Don't retry on error — if the table doesn't exist yet (migration not
    // applied), bouncing every 1s spams the network. Fall back to
    // tenant-only behavior (see below) until the migration lands.
    retry: false,
  });

  // While the tenant query is loading or pre-auth, return everything so
  // the sidebar groups stay visible during hydration. The override query
  // is allowed to be in-flight or errored — we treat both as "no
  // overrides yet" so deploys before the migration is applied don't
  // regress to "all modules visible".
  if (tenantQ.isLoading || !tenantQ.data) {
    return { enabled: new Set<ModuleKey>(MODULE_KEYS), isLoading: true };
  }

  // Map the user's own overrides by module_key. If the override query
  // errored or hasn't returned yet, this stays empty and we degrade to
  // tenant-only gating — same behavior as before this hook learned about
  // overrides. RLS already filters the overrides table so non-admins
  // only see their own rows; for admins we narrow down to user.id
  // explicitly so an admin viewing all rows still gets *their* gate
  // computed correctly.
  const overrideMap = new Map<ModuleKey, boolean>();
  if (overrideQ.data && user) {
    for (const o of overrideQ.data) {
      if (o.userId === user.id) {
        overrideMap.set(o.moduleKey as ModuleKey, o.isEnabled);
      }
    }
  }

  const enabled = new Set<ModuleKey>(ALWAYS_ON_MODULES);
  for (const row of tenantQ.data) {
    const key = row.moduleKey as ModuleKey;
    const override = overrideMap.get(key);
    const effective = override ?? row.isEnabled;
    if (effective) enabled.add(key);
  }
  return { enabled, isLoading: false };
}

/** Toggle a single module on/off for the current tenant. Optimistic
 *  update on the `module-settings` query so the sidebar reacts in the
 *  same render. On error we roll back to the previous snapshot. */
export function useUpdateModuleSetting() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleKey, isEnabled }: { moduleKey: ModuleKey; isEnabled: boolean }) =>
      ds.settings.updateModuleSetting(moduleKey, isEnabled),
    onMutate: async ({ moduleKey, isEnabled }) => {
      await qc.cancelQueries({ queryKey: MODULES_KEY });
      type Row = { moduleKey: string; isEnabled: boolean };
      const previous = qc.getQueryData<Row[]>(MODULES_KEY) ?? [];
      const next: Row[] = previous.some((r) => r.moduleKey === moduleKey)
        ? previous.map((r) => (r.moduleKey === moduleKey ? { ...r, isEnabled } : r))
        : [...previous, { moduleKey, isEnabled }];
      qc.setQueryData(MODULES_KEY, next);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(MODULES_KEY, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: MODULES_KEY }),
  });
}
